import React, { useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';

export const PRICE_PER_EMPLOYEE = 10.9;
export const COURSE_COUNT = 16;
export const eur = (n) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

export const HeroCalculator = () => {
  const [count, setCount] = useState(10);
  const [redirecting, setRedirecting] = useState(false);

  const setSafe = (v) => setCount(Math.min(500, Math.max(1, parseInt(v, 10) || 1)));

  const startCheckout = async () => {
    setRedirecting(true);
    try {
      const params = new URLSearchParams({ quantity: String(count), cycle: 'monthly' });
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/learning/checkout/basis?${params}`);
      const data = await res.json();
      if (!data.checkout_url) throw new Error(data.message || 'Kasse derzeit nicht erreichbar');
      window.location.href = data.checkout_url;
    } catch (err) {
      setRedirecting(false);
      toast.error('Kasse nicht erreichbar', { description: err.message });
    }
  };

  return (
    <div id="pricing" className="scroll-mt-24 rounded-2xl border border-yellow-500/40 bg-gray-950/80 backdrop-blur-md p-5 sm:p-6 max-w-xl mx-auto lg:mx-0 shadow-2xl shadow-yellow-500/10" data-testid="hero-calculator">
      <label htmlFor="hero-calc-input" className="block text-sm font-semibold text-gray-200 mb-3">
        Anzahl der Mitarbeiter in Ihrem Betrieb
      </label>
      <div className="flex items-center gap-4 mb-4">
        <Slider
          value={[Math.min(count, 50)]}
          min={1}
          max={50}
          step={1}
          onValueChange={(v) => setCount(v[0])}
          className="flex-1 [&>span:first-child]:bg-gray-700 [&>span:first-child>span]:bg-yellow-500 [&_[role=slider]]:bg-yellow-400 [&_[role=slider]]:border-yellow-300"
          data-testid="hero-calc-slider"
        />
        <input
          id="hero-calc-input"
          type="number"
          min={1}
          max={500}
          value={count}
          onChange={(e) => setSafe(e.target.value)}
          className="w-20 bg-gray-900 border border-gray-700 rounded-lg px-2 py-2 text-white text-center text-xl font-bold focus:border-yellow-500 focus:outline-none"
          data-testid="hero-calc-input"
        />
      </div>

      <div className="rounded-xl bg-yellow-500/10 border border-yellow-500/30 px-4 py-3 mb-4 flex flex-wrap items-baseline justify-center gap-x-2 text-center" data-testid="hero-calc-result">
        <span className="text-gray-100 text-base sm:text-lg font-semibold whitespace-nowrap">{count} Mitarbeiter =</span>
        <span className="text-3xl sm:text-4xl font-black text-yellow-400 whitespace-nowrap" data-testid="hero-calc-total">
          {eur(count * PRICE_PER_EMPLOYEE)} € <span className="text-sm font-medium text-gray-400">/ Monat</span>
        </span>
      </div>

      <Button
        onClick={startCheckout}
        disabled={redirecting}
        size="lg"
        className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-black text-lg py-6 hover:from-yellow-400 hover:to-amber-400 shadow-xl shadow-yellow-500/30 transition-shadow"
        data-testid="cta-test-btn"
      >
        {redirecting
          ? <><Loader2 className="mr-2 w-5 h-5 animate-spin" /> Weiter zur sicheren Kasse…</>
          : <>14-Tage-Testzugang starten <ArrowRight className="ml-2 w-5 h-5" /></>}
      </Button>

      <p className="mt-3 text-sm font-semibold text-white text-center" data-testid="hero-price-label">
        Nur 10,90 € pro Mitarbeiter / Monat (zzgl. MwSt.). 14 Tage kostenlos testen, jederzeit zum Monatsende kündbar.
      </p>
      <p className="mt-2 text-xs text-gray-400 text-center flex items-start justify-center gap-1.5" data-testid="hero-risk-reversal">
        <ShieldCheck className="w-4 h-4 text-green-400 shrink-0" />
        <span>14 Tage kostenlos testen – Voller Zugriff auf alle 16 Kurse &amp; rechtssichere Unterweisungen. Vor Testende mit einem Klick kündbar – dann zahlen Sie 0 €.</span>
      </p>
      <p className="mt-2 text-xs text-yellow-300/90 text-center" data-testid="hero-per-course">
        Das sind nur ca. {eur(PRICE_PER_EMPLOYEE / COURSE_COUNT)} € pro Mitarbeiter und Kurs im Monat.
      </p>
    </div>
  );
};

export default HeroCalculator;
